import React, { useEffect } from 'react'
import { Badge, Button, Table, Card, Row, Col, Typography, Space, Empty, Tag, Tooltip, Alert } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAdd,
  faTrash,
  faEdit,
  faStethoscope,
  faSyringe,
  faHeartPulse,
  faBandage,
  faInfoCircle,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA } from '../../../../lib/utils'
import {
  CLINIC_FORM_CATEGORY_ICONS,
  CLINIC_FORM_CATEGORY_COLORS,
  CLINIC_FORM_CATEGORY_LABELS
} from '../../config/form-categories'
import { renderMacroItems } from '../macro-items-renderer'
import { clinicTabsEnums } from '../../config/clinic-tabs-enums'

const { Title, Text, Paragraph } = Typography

export const createBillingMacrosTab = ({
  token,
  unfilledFields,
  handleNewMacro,
  handleMacroDelete,
  handleMacroClick,
  macros,
  disabled,
  billingMacrosForm
}) => {
  const commonMacroExamples = [
    {
      icon: faHeartPulse,
      name: 'ECG Package',
      description: 'Complete ECG procedure with electrodes, gloves, and interpretation'
    },
    {
      icon: faBandage,
      name: 'Suture Kit',
      description: 'Full wound closure package with procedures, materials, and local anesthesia'
    },
    {
      icon: faSyringe,
      name: 'IV Therapy',
      description: 'Comprehensive IV setup with all consumables, tubing, and solutions'
    },
    {
      icon: faStethoscope,
      name: 'Minor Procedure',
      description: 'Complete procedural package with all required materials and supplies'
    }
  ]

  const renderEmptyState = () => (
    <Card
      style={{
        borderRadius: '16px',
        border: `2px dashed ${hexToRGBA(token.colorBorder, 0.3)}`,
        backgroundColor: hexToRGBA(token.colorBgLayout, 0.3),
        textAlign: 'center',
        padding: '16px 20px'
      }}
    >
      <Empty image={null} description={null}>
        <Button
          type='primary'
          size='large'
          onClick={handleNewMacro}
          icon={<FontAwesomeIcon icon={faAdd} />}
          style={{
            borderRadius: '8px',
            height: '48px',
            padding: '0 32px',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          Create Your First Macro
        </Button>

        <div style={{ marginTop: '48px' }}>
          <Title level={5} style={{ color: token.colorTextHeading, margin: '0 0 16px 0' }}>
            Common Macro Examples:
          </Title>
          <Row gutter={[16, 16]} justify='center'>
            {commonMacroExamples.map((example, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card
                  size='small'
                  style={{
                    borderRadius: '12px',
                    border: `1px solid ${hexToRGBA(token.colorBorder, 0.2)}`,
                    backgroundColor: 'white',
                    textAlign: 'center',
                    height: '120px'
                  }}
                  bodyStyle={{ padding: '16px 12px' }}
                >
                  <FontAwesomeIcon
                    icon={example.icon}
                    style={{
                      fontSize: '24px',
                      color: token.colorPrimary,
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>{example.name}</div>
                  <div style={{ fontSize: '11px', color: token.colorTextSecondary, lineHeight: '1.3' }}>
                    {example.description}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Empty>
    </Card>
  )

  const renderMacrosTable = () => (
    <Card
      style={{
        borderRadius: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`
      }}
      bodyStyle={{ padding: '0' }}
    >
      <div style={{ padding: '24px 24px 0 24px' }}>
        <Row justify='space-between' align='middle' style={{ marginBottom: '20px' }}>
          <Col>
            <Space direction='vertical' size={4}>
              <Title level={4} style={{ margin: 0, color: token.colorTextHeading }}>
                Configured Billing Macros
              </Title>
              <Text type='secondary'>
                {macros.length} macro{macros.length !== 1 ? 's' : ''} configured for streamlined billing
              </Text>
            </Space>
          </Col>
          <Col>
            <Button
              type='primary'
              onClick={handleNewMacro}
              icon={<FontAwesomeIcon icon={faAdd} />}
              style={{
                borderRadius: '8px',
                height: '40px',
                padding: '0 20px',
                fontWeight: '500'
              }}
            >
              New Macro
            </Button>
          </Col>
        </Row>
      </div>

      <Table
        dataSource={macros}
        pagination={false}
        bordered={false}
        style={{
          borderRadius: '0 0 16px 16px',
          overflow: 'hidden'
        }}
        columns={[
          {
            title: 'Macro Name',
            dataIndex: 'name',
            key: 'name'
          },
          {
            title: 'Items',
            dataIndex: 'items',
            key: 'items',
            render: (items) => items.length
          },
          {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: '120px',
            render: (isActive) => (
              <Tag
                color={isActive ? 'success' : 'default'}
                style={{
                  borderRadius: '12px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Tag>
            )
          },
          {
            title: 'Actions',
            key: 'actions',
            width: '100px',
            render: (_, record, index) => (
              <Space>
                <Tooltip title='Edit macro'>
                  <Button
                    type='text'
                    size='small'
                    onClick={() => handleMacroClick(record, index)}
                    icon={<FontAwesomeIcon icon={faEdit} />}
                    style={{
                      color: token.colorPrimary,
                      borderRadius: '6px'
                    }}
                  />
                </Tooltip>
                <Tooltip title='Delete macro'>
                  <Button
                    type='text'
                    size='small'
                    danger
                    onClick={(e) => {
                      e.stopPropagation()
                      handleMacroDelete(index)
                    }}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    style={{
                      borderRadius: '6px'
                    }}
                  />
                </Tooltip>
              </Space>
            )
          }
        ]}
        expandable={{
          expandedRowRender: (record) => renderMacroItems(record.items),
          rowExpandable: (record) => (record.items?.length || 0) > 0,
          expandIcon: ({ expanded, onExpand, record }) =>
            (record.items?.length || 0) > 0 ? (
              <Button
                type='text'
                size='small'
                onClick={(e) => onExpand(record, e)}
                style={{
                  color: token.colorPrimary,
                  fontSize: '12px',
                  padding: '2px 8px',
                  height: 'auto',
                  borderRadius: '4px'
                }}
              >
                {expanded ? 'Hide Items' : 'View Items'}
              </Button>
            ) : null
        }}
        onRow={(record, rowIndex) => ({
          style: {
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          },
          onMouseEnter: (e) => {
            e.currentTarget.style.backgroundColor = hexToRGBA(token.colorPrimary, 0.02)
          },
          onMouseLeave: (e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }
        })}
        rowKey={(_, index) => index}
      />
    </Card>
  )

  return {
    key: clinicTabsEnums.BILLING_MACROS,
    disabled: disabled,
    label: (
      <span>
        <FontAwesomeIcon
          icon={CLINIC_FORM_CATEGORY_ICONS.BILLING_MACROS}
          style={{ marginRight: '8px', color: CLINIC_FORM_CATEGORY_COLORS.BILLING_MACROS }}
        />
        {CLINIC_FORM_CATEGORY_LABELS.BILLING_MACROS}
        <Badge count={unfilledFields[7]} style={{ marginLeft: '8px', backgroundColor: token.colorError }} />
      </span>
    ),
    children: (
      <div style={{ padding: '24px 0', background: hexToRGBA(token.colorBgLayout, 0.3) }}>
        <Row justify='center'>
          <Col xs={24} xl={20}>
            {/* Header Section */}
            <Card
              style={{
                borderRadius: '16px',
                marginBottom: '24px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f0fff4 100%)',
                border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`
              }}
              bodyStyle={{ padding: '32px' }}
            >
              <Row gutter={[24, 24]} align='middle'>
                <Col xs={24} lg={16}>
                  <Space direction='vertical' size={12}>
                    <div>
                      <Title level={3} style={{ margin: '0 0 8px 0', color: token.colorTextHeading }}>
                        <FontAwesomeIcon
                          icon={faClipboardList}
                          style={{ marginRight: '12px', color: token.colorSuccess }}
                        />
                        Billing Macros
                      </Title>
                      <Paragraph style={{ fontSize: '16px', margin: 0, color: token.colorTextSecondary }}>
                        Streamline your billing process by creating reusable service packages
                      </Paragraph>
                    </div>
                  </Space>
                </Col>
                <Col xs={24} lg={8}>
                  <Alert
                    message='Time-Saving Feature'
                    description='Group related services to avoid billing each line item individually'
                    type='info'
                    showIcon
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    style={{
                      borderRadius: '8px',
                      border: `1px solid ${hexToRGBA(token.colorInfo, 0.3)}`
                    }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Content Section */}
            {macros && macros.length > 0 ? renderMacrosTable() : renderEmptyState()}
          </Col>
        </Row>
      </div>
    )
  }
}
